<template>
  <div class="w-full">
    <!-- Disabled: Origin SbSoon — muted, inert, no navigation -->
    <div
      v-if="disabled"
      :class="itemClasses"
      role="presentation"
      aria-disabled="true"
      :aria-label="props['aria-label']"
    >
      <div class="flex items-center gap-2 flex-1 min-w-0">
        <span
          v-if="subSub"
          class="w-1 h-1 rounded-full bg-current opacity-50 flex-shrink-0"
          aria-hidden="true"
        />
        <BaseIcon
          v-else-if="$slots.icon"
          size="small"
          variant="ghost"
        >
          <slot name="icon" />
        </BaseIcon>
        <span class="text-left truncate">
          <slot name="content" />
        </span>
      </div>
    </div>
    <router-link
      v-else
      :to="to"
      :class="itemClasses"
      :aria-label="props['aria-label']"
      @click="handleClick"
    >
      <!-- Left side: dot when subSub, icon only when slot provided (design: icons only on menu names) -->
      <div class="flex items-center gap-2 flex-1 min-w-0">
        <span
          v-if="subSub"
          class="w-1 h-1 rounded-full bg-current opacity-50 flex-shrink-0"
          aria-hidden="true"
        />
        <BaseIcon
          v-else-if="$slots.icon"
          size="small"
          variant="ghost"
        >
          <slot name="icon" />
        </BaseIcon>
        <span class="text-left truncate">
          <slot name="content" />
        </span>
      </div>
    </router-link>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import BaseIcon from '@/components/ui/BaseIcon.vue'

interface Props {
  to?: string
  active?: boolean
  'aria-label'?: string
  /** Third-level item: show dot instead of icon (design sub-sub) */
  subSub?: boolean
  /** Ghost IA node — visible but non-navigating (Origin sb-item.soon) */
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  to: '#',
  active: false,
  subSub: false,
  disabled: false,
})

const route = useRoute()
const router = useRouter()

// Inject the reload function from App.vue
const reloadCurrentView = inject<() => void>('reloadCurrentView')
// Sidebar nav dense style (design: px-3 py-1.5 text-[13px])
const sidebarNavDense = inject<boolean>('sidebarNavDense', false)

const itemClasses = computed(() => {
  // Design: all nested/sublinks use px-3 py-1.5 rounded-md text-[13px]; top-level would use py-2
  const padding = sidebarNavDense ? 'px-3 py-1.5' : 'px-3 py-2'
  const textSize = sidebarNavDense ? 'text-[13px]' : 'text-sm'

  if (props.disabled) {
    return [
      'group flex items-center gap-2 flex-1 rounded-md',
      padding,
      textSize,
      'font-medium',
      'text-dark-text-muted opacity-40 cursor-default',
      'bg-transparent',
    ].join(' ')
  }

  // Design: first-level sub = text-dark-text-secondary hover:bg only; sub-sub = text-dark-text-muted hover:bg hover:text-dark-text-secondary
  const textColor = props.subSub
    ? 'text-dark-text-muted hover:bg-surface-hover hover:text-dark-text-secondary'
    : 'text-dark-text-secondary hover:bg-surface-hover'
  const baseClasses = [
    'group flex items-center gap-2 flex-1 rounded-md transition-colors duration-200',
    padding,
    textSize,
    'font-medium',
    textColor,
  ]

  if (props.active) {
    baseClasses.push('bg-sidebar-active text-sidebar-active')
  } else {
    baseClasses.push('bg-transparent')
  }

  return baseClasses.join(' ')
})

const handleClick = (event: MouseEvent) => {
  // If no 'to' prop or it's just '#', let default behavior happen
  if (!props.to || props.to === '#') {
    return
  }

  // Check if we're navigating to the current route
  const currentPath = route.path
  const targetPath = props.to

  // Normalize paths (remove trailing slashes for comparison)
  const normalizedCurrent = currentPath.replace(/\/$/, '')
  const normalizedTarget = targetPath.replace(/\/$/, '')

  // If clicking on the active route, force a reload
  if (normalizedCurrent === normalizedTarget && reloadCurrentView) {
    event.preventDefault()

    // Trigger reload by updating the router-view key
    // This will force Vue Router to remount the current component
    reloadCurrentView()
  } else if (normalizedCurrent !== normalizedTarget) {
    // Different route - ensure navigation happens
    // If router-link already prevented default (edge case), manually trigger navigation
    if (event.defaultPrevented) {
      // Router-link prevented default incorrectly - manually navigate
      event.preventDefault()
      router.push(targetPath).catch((err) => {
        // Ignore navigation duplicated errors
        if (err.name !== 'NavigationDuplicated') {
          console.error('Navigation error:', err)
        }
      })
    }
    // Otherwise, let router-link handle navigation normally
  }
}
</script>
